/**
 * 書き起こし表示
 *
 * 話者分離された書き起こしテキストを表示
 */

import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Chip,
  Divider,
  IconButton,
  Tooltip,
  FormControlLabel,
  Switch,
  TextField,
  InputAdornment
} from '@mui/material';
import {
  Person as PersonIcon,
  Business as BusinessIcon,
  Search as SearchIcon,
  ContentCopy as ContentCopyIcon,
  Check as CheckIcon
} from '@mui/icons-material';

/**
 * 話者セグメントの表示
 */
function SpeakerSegment({ segment, showTimestamp, isHighlighted }) {
  const isInterviewer = segment.speaker === 'interviewer';

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <Box
      sx={{
        display: 'flex',
        mb: 2,
        flexDirection: isInterviewer ? 'row' : 'row-reverse',
        backgroundColor: isHighlighted ? 'rgba(255, 235, 59, 0.2)' : 'transparent',
        borderRadius: 1,
        p: isHighlighted ? 1 : 0
      }}
    >
      {/* アバター */}
      <Box
        sx={{
          width: 40,
          height: 40,
          borderRadius: '50%',
          backgroundColor: isInterviewer ? 'primary.light' : 'secondary.light',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
          mx: 1
        }}
      >
        {isInterviewer ? (
          <BusinessIcon sx={{ color: 'white', fontSize: 20 }} />
        ) : (
          <PersonIcon sx={{ color: 'white', fontSize: 20 }} />
        )}
      </Box>

      {/* テキスト */}
      <Box sx={{ flex: 1, maxWidth: '70%' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
          <Typography variant="caption" fontWeight="bold" color="text.secondary">
            {isInterviewer ? '面接官' : '候補者'}
          </Typography>
          {showTimestamp && (
            <Typography variant="caption" color="text.disabled" sx={{ ml: 1 }}>
              {formatTime(segment.start)} - {formatTime(segment.end)}
            </Typography>
          )}
          {segment.confidence && (
            <Chip
              label={`${(segment.confidence * 100).toFixed(0)}%`}
              size="small"
              variant="outlined"
              sx={{ ml: 1, height: 18, fontSize: '0.65rem' }}
            />
          )}
        </Box>
        <Paper
          variant="outlined"
          sx={{
            p: 1.5,
            backgroundColor: isInterviewer ? 'grey.50' : 'primary.50',
            borderColor: isInterviewer ? 'grey.300' : 'primary.200'
          }}
        >
          <Typography variant="body2">{segment.text}</Typography>
        </Paper>
      </Box>
    </Box>
  );
}

/**
 * 書き起こし表示コンポーネント
 */
export default function TranscriptionView({ transcription, highlights }) {
  const [showTimestamp, setShowTimestamp] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [copied, setCopied] = useState(false);

  if (!transcription) {
    return (
      <Typography color="text.secondary">
        書き起こしデータがありません
      </Typography>
    );
  }

  const speakerSegments = transcription.speaker_segments || [];
  const fillers = transcription.fillers || [];
  const fullText = transcription.full_text || '';

  // ハイライト区間に含まれるかチェック
  const isInHighlight = (segment) => {
    if (!highlights || highlights.length === 0) return false;
    return highlights.some(
      (h) => segment.start >= h.start && segment.end <= h.end
    );
  };

  // 検索フィルター
  const filteredSegments = searchQuery
    ? speakerSegments.filter((seg) =>
        seg.text.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : speakerSegments;

  // コピー機能
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(fullText);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  // フィラー統計
  const fillerStats = fillers.reduce((acc, f) => {
    acc[f.type] = (acc[f.type] || 0) + 1;
    return acc;
  }, {});

  return (
    <Box>
      {/* ヘッダー */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
        {/* 検索 */}
        <TextField
          size="small"
          placeholder="検索..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            )
          }}
          sx={{ width: 200 }}
        />

        {/* タイムスタンプ表示切替 */}
        <FormControlLabel
          control={
            <Switch
              checked={showTimestamp}
              onChange={(e) => setShowTimestamp(e.target.checked)}
              size="small"
            />
          }
          label="タイムスタンプ"
        />

        {/* コピーボタン */}
        <Tooltip title={copied ? 'コピーしました' : '全文をコピー'}>
          <IconButton onClick={handleCopy} size="small">
            {copied ? <CheckIcon color="success" /> : <ContentCopyIcon />}
          </IconButton>
        </Tooltip>

        <Box sx={{ flex: 1 }} />

        {/* 統計 */}
        <Typography variant="caption" color="text.secondary">
          {speakerSegments.length}セグメント
        </Typography>
        {transcription.total_word_count && (
          <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
            約{transcription.total_word_count}語
          </Typography>
        )}
      </Box>

      {/* フィラー統計 */}
      {Object.keys(fillerStats).length > 0 && (
        <Paper variant="outlined" sx={{ p: 1.5, mb: 2 }}>
          <Typography variant="caption" color="text.secondary" sx={{ mr: 1 }}>
            検出されたフィラー:
          </Typography>
          {Object.entries(fillerStats).map(([type, count]) => (
            <Chip
              key={type}
              label={`${type}: ${count}回`}
              size="small"
              variant="outlined"
              sx={{ mr: 0.5, mb: 0.5 }}
            />
          ))}
        </Paper>
      )}

      <Divider sx={{ mb: 2 }} />

      {/* セグメント一覧 */}
      <Box sx={{ maxHeight: 500, overflow: 'auto' }}>
        {filteredSegments.length > 0 ? (
          filteredSegments.map((segment, index) => (
            <SpeakerSegment
              key={index}
              segment={segment}
              showTimestamp={showTimestamp}
              isHighlighted={isInHighlight(segment)}
            />
          ))
        ) : (
          <Typography color="text.secondary" textAlign="center">
            {searchQuery ? '検索結果がありません' : 'セグメントがありません'}
          </Typography>
        )}
      </Box>

      {/* 全文表示（折りたたみ） */}
      {fullText && (
        <Box sx={{ mt: 2 }}>
          <Divider sx={{ mb: 2 }} />
          <Typography variant="subtitle2" gutterBottom>
            全文
          </Typography>
          <Paper
            variant="outlined"
            sx={{
              p: 2,
              maxHeight: 200,
              overflow: 'auto',
              backgroundColor: 'grey.50'
            }}
          >
            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
              {transcription.masked_text || fullText}
            </Typography>
          </Paper>
        </Box>
      )}
    </Box>
  );
}
